import { Form } from 'antd';
import type { FormItemProps } from 'components/Form/FormItem';
import { useMeterContext } from 'contexts/Meter';
import { isNil } from 'rambda';
import React from 'react';
import { MeterDetailGas, MeterDetailOther } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import { Nullable } from '@enmon/commons';
import { useStore } from '../../../useStore';
import { ElectricityAdditionalFields, MeterElectricityDetailsProps } from './Electricity';
import { GasAdditionalFields } from './Gas';
import { ThermalAdditionalFields, MeterThermalDetailsProps } from './Thermal';
import { OtherAdditionalFields } from './Other';
import { MeterWaterDetailsProps, WaterAdditionalFields } from './Water';
import { isFalseImportField } from '../../utils/isFalseImportField';
import { DetailStepForm } from '../slice';

export const AdditionalFields: React.VFC<{
  commodity: Nullable<Commodity>;
}> = ({ commodity }) => {
  const { detail, setDetailStepValues } = useStore();

  const { inventoryMeter } = useMeterContext();
  const meterId = inventoryMeter?.id;

  const form = Form.useFormInstance<DetailStepForm>();

  const handleDetailsFormItemReset = (namePath: FormItemProps['name']) => {
    const name = Array.isArray(namePath) ? namePath.at(-1) : namePath;

    if (isNil(namePath) || isNil(name)) return;

    const resetValue = isFalseImportField(commodity, name.toString()) ? false : null;

    form.setFieldValue(namePath, resetValue);
    setDetailStepValues({
      detail: {
        ...detail,
        [name]: resetValue,
      },
    });
  };

  switch (commodity) {
    case Commodity.Thermal:
      return (
        <ThermalAdditionalFields
          {...({
            ...detail,
            onFormItemReset: handleDetailsFormItemReset,
            meterId,
          } as MeterThermalDetailsProps)}
        />
      );
    case Commodity.Water:
      return (
        <WaterAdditionalFields
          {...({
            ...detail,
            onFormItemReset: handleDetailsFormItemReset,
            meterId,
          } as MeterWaterDetailsProps)}
        />
      );
    case Commodity.Electricity:
      return (
        <ElectricityAdditionalFields
          {...({
            ...detail,
            onFormItemReset: handleDetailsFormItemReset,
            meterId,
          } as MeterElectricityDetailsProps)}
        />
      );
    case Commodity.NaturalGas:
      return (
        <GasAdditionalFields
          meterId={meterId}
          onFormItemReset={handleDetailsFormItemReset}
          {...(detail as MeterDetailGas)}
        />
      );
    case Commodity.Other:
      return <OtherAdditionalFields {...(detail as MeterDetailOther)} />;
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      console.error(`Third step with unknown commodity: ${commodity ?? 'undefined'}`);
      return null;
  }
};
